import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { COMPANY_NAME, COMPANY_INITIALS } from "@/lib/brand";
import { Printer, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const SORT_OPTIONS = [
  { value: "fifo", label: "Order Date (FIFO)" },
  { value: "width_asc", label: "Width (Small → Large)" },
  { value: "width_desc", label: "Width (Large → Small)" },
  { value: "micron_asc", label: "Micron (Thin → Thick)" },
  { value: "micron_desc", label: "Micron (Thick → Thin)" },
  { value: "color", label: "Color" },
  { value: "machine", label: "Machine Grade" },
  { value: "customer", label: "Customer" },
  { value: "manual", label: "Manual (drag order)" },
];

export default function WorkOrdersBatch() {
  const [rows, setRows] = useState(null);
  const [sortBy, setSortBy] = useState("fifo");
  const [manualOrder, setManualOrder] = useState([]); // array of row keys
  const [groupByOrder, setGroupByOrder] = useState(true);

  useEffect(() => {
    async function load() {
      const [o, c, p] = await Promise.all([
        api.get("/orders"),
        api.get("/customers"),
        api.get("/products"),
      ]);
      const open = o.data.filter((x) => ["pending", "in_production", "ready"].includes(x.status));
      open.sort((a, b) => (a.order_date || "").localeCompare(b.order_date || ""));
      const custById = {}; c.data.forEach((cu) => { custById[cu.id] = cu; });
      const productById = {}; p.data.forEach((pr) => { productById[pr.id] = pr; });
      const aMap = {};
      await Promise.all(open.map(async (ord) => {
        try {
          const r = await api.get(`/orders/${ord.id}/assigned`);
          aMap[ord.id] = {};
          (r.data.cartons || []).forEach((cn) => {
            aMap[ord.id][cn.product_id] = (aMap[ord.id][cn.product_id] || 0) + 1;
          });
        } catch { /* no-op */ }
      }));
      const flat = [];
      open.forEach((ord, ordIdx) => {
        const cust = custById[ord.customer_id] || {};
        (ord.items || []).filter((it) => it.product_id).forEach((it, lineIdx) => {
          const pr = productById[it.product_id] || {};
          const spec = { ...(pr.spec || {}), ...(it.spec || {}) };
          const assigned = (aMap[ord.id] && aMap[ord.id][it.product_id]) || 0;
          const toProduce = Math.max(0, (it.quantity_rolls || 0) - assigned);
          flat.push({
            key: `${ord.id}-${lineIdx}`,
            ordIdx,
            order_no: ord.order_no,
            order_date: ord.order_date,
            delivery_date: ord.delivery_date,
            customer: cust.company || cust.name || "—",
            status: ord.status,
            sku: pr.sku || "—",
            thickness_micron: Number(spec.thickness_micron) || 0,
            width_mm: Number(spec.width_mm) || 0,
            roll_length_m: Number(spec.roll_length_m) || 0,
            color: spec.color || "—",
            grade: spec.grade || "—",
            spec: `${spec.thickness_micron || "?"}µ · ${spec.width_mm || "?"}mm${spec.roll_length_m ? ` · ${spec.roll_length_m}m` : ""}`,
            rpc: spec.rolls_per_carton || 1,
            required: it.quantity_rolls || 0,
            assigned,
            toProduce,
            lineIdx,
            linesInOrder: (ord.items || []).filter((x) => x.product_id).length,
          });
        });
      });
      setRows(flat);
      setManualOrder(flat.map((r) => r.key));
    }
    load();
  }, []);

  // Compute the displayed rows based on the chosen sort.
  const displayedRows = useMemo(() => {
    if (!rows) return null;
    const list = [...rows];
    const cmpStr = (a, b, k) => (a[k] || "").toString().localeCompare((b[k] || "").toString());
    const cmpNum = (a, b, k) => (a[k] || 0) - (b[k] || 0);
    if (sortBy === "fifo") {
      list.sort((a, b) =>
        cmpStr(a, b, "order_date") || cmpStr(a, b, "order_no") || (a.lineIdx - b.lineIdx)
      );
    } else if (sortBy === "width_asc") {
      list.sort((a, b) => cmpNum(a, b, "width_mm") || cmpNum(a, b, "thickness_micron"));
    } else if (sortBy === "width_desc") {
      list.sort((a, b) => cmpNum(b, a, "width_mm") || cmpNum(a, b, "thickness_micron"));
    } else if (sortBy === "micron_asc") {
      list.sort((a, b) => cmpNum(a, b, "thickness_micron") || cmpNum(a, b, "width_mm"));
    } else if (sortBy === "micron_desc") {
      list.sort((a, b) => cmpNum(b, a, "thickness_micron") || cmpNum(a, b, "width_mm"));
    } else if (sortBy === "color") {
      list.sort((a, b) => cmpStr(a, b, "color") || cmpStr(a, b, "sku"));
    } else if (sortBy === "machine") {
      list.sort((a, b) => cmpStr(a, b, "grade") || cmpNum(a, b, "width_mm"));
    } else if (sortBy === "customer") {
      list.sort((a, b) => cmpStr(a, b, "customer") || cmpStr(a, b, "order_date"));
    } else if (sortBy === "manual") {
      const idx = new Map(manualOrder.map((k, i) => [k, i]));
      list.sort((a, b) => (idx.get(a.key) ?? 0) - (idx.get(b.key) ?? 0));
    }
    // Decide group/firstLine flag — only meaningful when sorting FIFO (by order)
    // For other sorts each row is independent, so always show order info.
    const grouped = sortBy === "fifo" && groupByOrder;
    let lastOrder = "";
    return list.map((r) => {
      const isFirst = grouped ? r.order_no !== lastOrder : true;
      lastOrder = r.order_no;
      return { ...r, firstLine: isFirst, ungrouped: !grouped };
    });
  }, [rows, sortBy, manualOrder, groupByOrder]);

  if (rows === null) return <div className="p-8 font-mono text-xs text-zinc-500">LOADING OPEN WORK ORDERS...</div>;
  if (rows.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="bg-white border border-zinc-300 p-8 max-w-md text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">No Open Work Orders</div>
          <h1 className="text-2xl font-bold text-zinc-950 mb-4">All caught up</h1>
          <a href="/orders" className="text-[11px] uppercase tracking-wider font-mono px-4 py-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-100 inline-block">← Back to Orders</a>
        </div>
      </div>
    );
  }

  const totalToProduce = displayedRows.reduce((s, r) => s + r.toProduce, 0);
  const totalRequired = displayedRows.reduce((s, r) => s + r.required, 0);
  const totalAssigned = displayedRows.reduce((s, r) => s + r.assigned, 0);
  const uniqueOrders = new Set(displayedRows.map((r) => r.order_no)).size;
  const currentSortLabel = SORT_OPTIONS.find((s) => s.value === sortBy)?.label || "FIFO";

  const moveRow = (key, dir) => {
    setManualOrder((prev) => {
      const idx = prev.indexOf(key);
      if (idx < 0) return prev;
      const target = dir === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-zinc-200 print:bg-white" data-testid="work-orders-batch">
      <div className="print:hidden bg-zinc-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-50 flex-wrap gap-3">
        <div className="font-mono text-xs uppercase tracking-widest">
          WORK ORDERS BATCH · {uniqueOrders} open · {totalToProduce} ctns to produce
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono">
            <ArrowUpDown size={12} /> Sort
            <select
              data-testid="sort-by-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-zinc-900 bg-white px-2 py-1 text-[11px] font-mono"
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
          {sortBy === "fifo" && (
            <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono">
              <input
                type="checkbox"
                data-testid="group-by-order"
                checked={groupByOrder}
                onChange={(e) => setGroupByOrder(e.target.checked)}
              />
              Group by order
            </label>
          )}
          <button
            onClick={() => window.print()}
            data-testid="batch-print-btn"
            className="text-[11px] uppercase font-mono px-4 py-1.5 bg-orange-600 hover:bg-orange-700 flex items-center gap-2"
          >
            <Printer size={12} /> Print / Save PDF
          </button>
        </div>
      </div>

      <div className="max-w-[210mm] mx-auto my-6 print:my-0 bg-white border border-zinc-300 print:border-0 shadow print:shadow-none">
        <div className="p-8 print:p-6">
          <div className="flex items-start justify-between border-b-2 border-zinc-900 pb-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-orange-600 flex items-center justify-center text-white font-mono font-bold text-sm">{COMPANY_INITIALS}</div>
              <div>
                <div className="text-base font-bold tracking-tight text-zinc-950">{COMPANY_NAME}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                  Open Work Orders · Sort: {currentSortLabel}
                </div>
              </div>
            </div>
            <div className="text-right text-xs font-mono">
              <div className="text-zinc-700">Printed: {new Date().toLocaleString("en-IN")}</div>
              <div className="text-zinc-700">Orders: {uniqueOrders} · Required: <span className="text-zinc-900 font-semibold">{totalRequired}</span> · In Stock: {totalAssigned} · <span className="text-orange-700 font-semibold">To Produce: {totalToProduce}</span></div>
            </div>
          </div>

          <table className="w-full text-[11px] border-collapse">
            <thead className="bg-zinc-100 border-y border-zinc-900">
              <tr className="font-mono uppercase tracking-wider text-[8px] text-zinc-700">
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">#</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">Order No</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">Customer</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">Order Dt</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">Delivery</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">SKU</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">Spec</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">Color</th>
                <th className="text-left px-1.5 py-1.5 border border-zinc-300">Grade</th>
                <th className="text-right px-1.5 py-1.5 border border-zinc-300">R/C</th>
                <th className="text-right px-1.5 py-1.5 border border-zinc-300">Req</th>
                <th className="text-right px-1.5 py-1.5 border border-zinc-300">Stock</th>
                <th className="text-right px-1.5 py-1.5 border border-zinc-300 bg-orange-100 text-orange-800">To Produce</th>
                {sortBy === "manual" && (
                  <th className="text-center px-1.5 py-1.5 border border-zinc-300 print:hidden">Move</th>
                )}
              </tr>
            </thead>
            <tbody className="font-mono">
              {displayedRows.map((r, i) => (
                <tr
                  key={r.key}
                  data-testid={`wo-row-${r.key}`}
                  className={`border-b border-zinc-200 ${r.firstLine && i > 0 && !r.ungrouped ? "border-t-2 border-t-zinc-400" : ""}`}
                >
                  <td className="px-1.5 py-1 border border-zinc-300 text-zinc-500">{i + 1}</td>
                  <td className="px-1.5 py-1 border border-zinc-300">{r.firstLine ? <span className="font-semibold text-zinc-900">{r.order_no}</span> : <span className="text-zinc-400">↳</span>}</td>
                  <td className="px-1.5 py-1 border border-zinc-300">{r.firstLine ? r.customer : ""}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-[10px]">{r.firstLine ? r.order_date : ""}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-[10px] text-orange-700">{r.firstLine ? r.delivery_date : ""}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 font-semibold">{r.sku}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-[10px] text-zinc-600">{r.spec}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-[10px] text-zinc-700">{r.color}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-[10px] text-zinc-700">{r.grade}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-right">{r.rpc}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-right">{r.required}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-right text-zinc-600">{r.assigned}</td>
                  <td className="px-1.5 py-1 border border-zinc-300 text-right bg-orange-50 text-orange-800 font-bold">{r.toProduce}</td>
                  {sortBy === "manual" && (
                    <td className="px-1 py-1 border border-zinc-300 text-center print:hidden whitespace-nowrap">
                      <button
                        data-testid={`move-up-${r.key}`}
                        onClick={() => moveRow(r.key, "up")}
                        disabled={i === 0}
                        className="inline-flex items-center justify-center w-5 h-5 border border-zinc-300 hover:bg-zinc-100 disabled:opacity-30 mr-0.5"
                        title="Move up"
                      >
                        <ArrowUp size={10} />
                      </button>
                      <button
                        data-testid={`move-down-${r.key}`}
                        onClick={() => moveRow(r.key, "down")}
                        disabled={i === displayedRows.length - 1}
                        className="inline-flex items-center justify-center w-5 h-5 border border-zinc-300 hover:bg-zinc-100 disabled:opacity-30"
                        title="Move down"
                      >
                        <ArrowDown size={10} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="font-mono">
              <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                <td colSpan={10} className="px-1.5 py-1.5 border border-zinc-300 uppercase text-[10px] font-semibold">Grand Total — {uniqueOrders} orders, {displayedRows.length} line items</td>
                <td className="px-1.5 py-1.5 border border-zinc-300 text-right font-semibold">{totalRequired}</td>
                <td className="px-1.5 py-1.5 border border-zinc-300 text-right">{totalAssigned}</td>
                <td className="px-1.5 py-1.5 border border-zinc-300 text-right bg-orange-100 text-orange-800 font-bold">{totalToProduce}</td>
                {sortBy === "manual" && <td className="print:hidden border border-zinc-300" />}
              </tr>
            </tfoot>
          </table>

          <div className="mt-4 text-[9px] font-mono uppercase tracking-widest text-zinc-500 text-center">
            Sort: {currentSortLabel} · Status filter: pending / in_production / ready · {COMPANY_NAME}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 8mm; }
          body { background: white !important; }
          table { font-size: 9px !important; }
          tr { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
